import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('funeralInstructions');
export interface FuneralInstructionsRec {
  id: string;
  userId: string;
  instructions?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

class FuneralInstructionsService {
  async getAllFuneralInstructions(): Promise<FuneralInstructionsRec[]> {
    const response = await http.get('/api/funeral-instructions');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch funeral instructions');
    }

    return response.data.data || [];
  }

  async getFuneralInstructionsById(id: string): Promise<FuneralInstructionsRec | null> {
    try {
      const response = await http.get(`/api/funeral-instructions/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch funeral instructions');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching funeral instructions by ID:', error);
      return null;
    }
  }

  async createFuneralInstructions(instructionsData: Partial<FuneralInstructionsRec>): Promise<FuneralInstructionsRec> {
    const response = await http.post('/api/funeral-instructions', instructionsData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save funeral instructions');
    }

    return response.data.data;
  }

  async updateFuneralInstructions(id: string, instructionsData: Partial<FuneralInstructionsRec>): Promise<FuneralInstructionsRec> {
    const response = await http.patch(`/api/funeral-instructions/${id}`, instructionsData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update funeral instructions');
    }

    return response.data.data;
  }

  async deleteFuneralInstructions(id: string): Promise<void> {
    const response = await http.delete(`/api/funeral-instructions/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete funeral instructions');
    }
  }

  async lockFuneralInstructions(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for funeral instructions ID:', id);
    const response = await http.post(`/api/funeral-instructions/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock funeral instructions');
    }
  }

  async unlockFuneralInstructions(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for funeral instructions ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/funeral-instructions/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock funeral instructions');
    }
  }

  async getFuneralInstructionsAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/funeral-instructions/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch funeral instructions access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching funeral instructions access grants:', error);
      return [];
    }
  }

  async deleteFuneralInstructionsAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/funeral-instructions/${id}/access-grants/${grantId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }
}

export const funeralInstructionsService = new FuneralInstructionsService();